import { DAYS_OF_WEEK } from "../src/View/UI/Scheduler/Scheduler";
export default class SchedulerBlockUtils {
    /**
     * Gets a time24 value compaitble with scheduler blocks
     *
     * @example
     * getTime24(3, 30) // returns 3.5
     * @example
     * getTime24(14, 0) // returns 14
     * @example
     * getTime24(5, 15) //returns 5.25
     * @param {number} hours24 - hour of day
     * @param {number} minutes - num minutes in hour
     * @returns {number} scheduler block compatible time value
     */
    static getTime24(hours24, minutes) {
        return hours24 + minutes / 60;
    }
    /**
     * Merges conflicting blocks together.  For example, Let's say that we have two blocks, both on Monday, one from 1PM
     * to 2PM (Block A), and another from 1:45PM to 3PM (Block B).  This method produces three blocks - 1PM to 1:45PM, 1:45PM to 2PM, 2PM to 3PM.
     * Blocks are passed in with a payload and these payloads are grouped together and passed back.  So, with our example,
     *<br/><br/>
     * 1:00PM - 1:45PM would get [payload A] <br/>
     * 1:45PM - 2:00PM would get [payload A, payload B] <br/>
     * 2:00PM - 3:00PM would get [payload B] <br/>
     *
     * @example
     * const blocks = [
     *    {startTime24: 13, endTime24: 13.75, days: new Set(['mon']), payload: {title: 'A'},
     *    {startTime24: 13.75, endTime24: 14.00, days: new Set(['mon']), payload: {title: 'B'}
     * ];
     * SchedulerBlockUtils.merge(blocks, (payloads, maxLen) => <SchedulerBlock title={payloads.map(p => p.title).join(', ') + maxLen}/>)
     * // Returns three blocks, 1pm - 1:45pm with title "A2", 1:45PM - 2:00pm with title "A, B2", 2:00pm to 3:00pm with title "B2"
     * @param {object} blocks - blocks with payloads
     * @param {Function} getContent - get content of blocks from payloads. Accepts the payload for a block as well as the number of payloads in the block with the most payloads.
     * @param {string} getPayloadId - sometimes two blocks actually refer to the same logical thing with slightly different values.  When this happens, it's common
     * to want to group the payloads by some id.  This doesn't automatically group the payloads (because always dealing with 2-dimensional arrays would be annoying) but
     * it does adjust the maxPayloadLength so that blocks that overlap but have the same id in the payload will only be counted once.
     * @returns {Array<Block>} merged blocks
     */
    static merge(blocks, getContent, getPayloadId) {
        // BlockBoundaries represent the start and end of each block.  We're going to map the blocks
        // to elements of this type and then use them to group together the payloads.
        const intermediaries = [];
        Object.keys(DAYS_OF_WEEK).forEach((day) => {
            blocks
                .filter((b) => b.days.has(day))
                .forEach((b, i) => {
                // Push the start and end of this block
                intermediaries.push({
                    time: b.startTime24,
                    isEnd: false,
                    payload: b.payload,
                    dayOfWeek: day,
                    // Id uniquely identifies this block.  Used for deleting payloads when the block ends
                    id: day + i,
                });
                intermediaries.push({
                    time: b.endTime24,
                    isEnd: true,
                    dayOfWeek: day,
                    // Id uniquely identifies this block.  Used for deleting payloads when the block ends
                    id: day + i,
                });
            });
        });
        // Group into merge blocks.  This is where we figure out how payloads should be grouped, and create the actual merged blocks.
        const mergeBlocks = [];
        Object.keys(DAYS_OF_WEEK).forEach((day) => {
            const sortedBlocksInDay = intermediaries
                .filter((i) => i.dayOfWeek === day)
                .sort((a, b) => a.time - b.time);
            // List of the payloads keyed by an id.  This id helps us know which payloads we should remove as blocks end.
            const payloadList = {};
            // The first block is a special case.  Do that one first.
            const firstIntermediary = sortedBlocksInDay[0];
            // The first one shouldn't be an end.  The second check is included mostly for type checking
            if (firstIntermediary && !firstIntermediary.isEnd) {
                let time = sortedBlocksInDay[0].time;
                payloadList[firstIntermediary.id] = firstIntermediary.payload;
                // Add the rest of the blocks
                sortedBlocksInDay.slice(1).forEach((sb) => {
                    // If there's payloads, make a block and push it onto the list.
                    if (Object.values(payloadList).length) {
                        mergeBlocks.push({
                            start: time,
                            end: sb.time,
                            dayOfWeek: day,
                            payloads: Object.values(payloadList),
                        });
                    }
                    // Update payload list
                    if (sb.isEnd) {
                        delete payloadList[sb.id];
                    }
                    else {
                        payloadList[sb.id] = sb.payload;
                    }
                    time = sb.time;
                });
            }
        });
        const maxPayloadLength = Math.max(...mergeBlocks.map((mb) => {
            if (getPayloadId) {
                const ids = mb.payloads.map((payload) => getPayloadId(payload));
                // find all the payloads that have a null value for id.
                const nonNulls = ids.filter((id) => id);
                const nullCount = ids.length - nonNulls.length;
                const uniqueCount = new Set(nonNulls).size;
                return nullCount + uniqueCount;
            }
            return mb.payloads.length;
        }));
        // Transform MergedBlocks into Blocks.
        return mergeBlocks.map((block) => {
            let groupedPayloads;
            if (getPayloadId) {
                groupedPayloads = Object.values(block.payloads.reduce((sum, payload) => {
                    const id = getPayloadId(payload);
                    return Object.assign(Object.assign({}, sum), { [id]: sum[id] ? sum[id].concat([payload]) : [payload] });
                }, {}));
            }
            else {
                groupedPayloads = block.payloads.map((payload) => [payload]);
            }
            const contentInfo = {
                start: block.start,
                end: block.end,
                groupedPayloads,
                maxPayloadLength,
            };
            return {
                startTime24: block.start,
                endTime24: block.end,
                days: new Set([block.dayOfWeek]),
                content: getContent(block.payloads, contentInfo),
            };
        });
    }
}
